import React from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { Badge, Kind as BadgeKind, Button, ButtonProps } from '@ha/dsl';

import { useIntl } from 'ha/i18n';
import { useLocation } from 'ha/helpers/router';
import FilterOverlay from 'ha/components/Filters/FilterOverlay';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

import FilterIcon from 'custom-icons/filter.svg';

import { getFilterValues } from '../../selectors/filterSelectors';
import SearchFiltersButtonContent from './SearchFiltersButtonContent';
import getFilterTagsCount from '../../utils/getFilterTagsCount';
import useQueryParams from '../../hooks/useQueryParams';

const useStyles = makeStyles(theme => ({
  buttonText: {
    marginRight: theme.spacing(1),
  },
  inlineBadge: {
    marginRight: '-0.3rem',
  },
  mobileControlBadge: {
    top: '5px',
  },
  mobileControlButton: {
    height: '2.5rem',
  },
}));

interface Props {
  withDateFilter?: boolean;
  kind?: 'default' | 'mobileControl';
  buttonSize?: ButtonProps['size'];
}

const SearchFiltersButton: React.FC<Props> = ({
  withDateFilter,
  kind = 'default',
  buttonSize = 'medium',
}) => {
  const { T } = useIntl();
  const classes = useStyles();
  const location = useLocation();
  const { removeUpdateSearchAlertParam } = useQueryParams();
  const { md } = useMediaQuery();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!md && buttonRef && location.query?.updateSearchAlert) {
      setAnchorEl(buttonRef.current);
    }
  }, [md, buttonRef]);

  const handleOpenClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    },
    [],
  );
  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const closeAndClearParams = React.useCallback(() => {
    removeUpdateSearchAlertParam();
    handleClose();
  }, [removeUpdateSearchAlertParam, handleClose]);

  const filterValues = useSelector(getFilterValues);

  const badgesCount = getFilterTagsCount({
    ...filterValues,
    dates: withDateFilter ? filterValues.dates : undefined,
    price: filterValues.price,
  });

  const isActive = badgesCount > 0;

  return (
    <React.Fragment>
      <Button
        ref={buttonRef}
        variant={isActive ? 'contained' : 'outlined'}
        size={buttonSize}
        className={
          kind === 'mobileControl' ? classes.mobileControlButton : undefined
        }
        color="primary"
        data-test-locator="Other Filter Button"
        onClick={handleOpenClick}
        startIcon={kind === 'default' ? <FilterIcon /> : undefined}
      >
        <Badge
          kind={kind === 'default' ? BadgeKind.inline : BadgeKind.mui}
          badgeContent={badgesCount}
          showZero={kind === 'default'}
          color={isActive ? 'white' : 'primary'}
          className={kind === 'default' ? classes.inlineBadge : undefined}
          badgeClassName={
            kind === 'mobileControl' ? classes.mobileControlBadge : undefined
          }
        >
          <div className={kind === 'default' ? classes.buttonText : undefined}>
            {T('Filters')}
          </div>
        </Badge>
      </Button>
      <FilterOverlay
        open={Boolean(anchorEl)}
        onClose={closeAndClearParams}
        onCloseTestLocator="Close Button - Search Filters"
        {...(md ? { type: 'popover', anchorEl } : { type: 'dialog' })}
      >
        <SearchFiltersButtonContent
          withDateFilter={withDateFilter}
          onShowResultsClick={handleClose}
        />
      </FilterOverlay>
    </React.Fragment>
  );
};

export default SearchFiltersButton;
